import {REG} from "/@lib@/commons/registry.js";
import {BaseElement, OSkinableInit} from "/@back@/commons/basis.js";
import {Button} from "/@back@/commons/widgets/buttons.js";
import {DOM, JSX} from "/@lib@/commons/xml/dom.js";
import {DOMSH} from "/@lib@/commons/xml/domsh.js";

/**
 * Widget gérant une zone scrollable dans une seule direction: horizontale ou verticale.
 *
 * State live attributes: vertical
 */
export interface ArrowScrollBox extends BaseElement {
	initialize(init?: OArrowScrollBoxInit): this
}

export interface OArrowScrollBoxInit extends OSkinableInit {
	vertical?: boolean

	/** Notifié à chaque changement d'état d'overflow/underflow. */
	cbOverflow?: (overflow: boolean) => void
}

export class ArrowScrollBox extends BaseElement {
	cbOverflow: (overflow: boolean) => void;

	get vertical(): boolean {return this.hasAttribute('vertical')}

	set vertical(val: boolean) {
		if (DOM.setAttrBool(this, 'vertical', val)) this.refresh();
	}

	scrollToChild(child: HTMLElement) {
		//scroll parfois en echec au refresh d'une page, => setTimeout
		setTimeout(() => {
			if (this.vertical) {
				this._content.scrollTop = child.offsetTop;
			} else {
				const ctnLeft = this._content.scrollLeft;
				const tabLeft = child.offsetLeft;
				if (tabLeft < ctnLeft) {
					this._content.scrollLeft = tabLeft;
				} else {
					const newLeft = tabLeft + child.offsetWidth - this._content.clientWidth;
					if (newLeft > ctnLeft) this._content.scrollLeft = newLeft;
				}
			}
		}, 1);
	}

	moveForward() {
		if (this.vertical) {
			this._content.scrollTop += this._content.clientHeight / this.children.length;
		} else {
			if (window.getComputedStyle(this).direction === 'rtl')
				this._content.scrollLeft -= this._content.clientWidth / this.children.length;
			else
				this._content.scrollLeft += this._content.clientWidth / this.children.length;
		}
	}

	moveBackward() {
		if (this.vertical) {
			this._content.scrollTop -= this._content.clientHeight / this.children.length;
		} else {
			if (window.getComputedStyle(this).direction === 'rtl')
				this._content.scrollLeft += this._content.clientWidth / this.children.length;
			else
				this._content.scrollLeft -= this._content.clientWidth / this.children.length;
		}
	}

	get isOverflow(): boolean {return this.vertical ? this._content.scrollHeight > this._content.clientHeight : this._content.scrollWidth > this._content.clientWidth}

	protected _start: HTMLDivElement;
	protected _end: HTMLDivElement;
	protected _startBtn: Button;
	protected _endBtn: Button;
	protected _content: HTMLDivElement;

	protected _initialize(init: OArrowScrollBoxInit) {
		this.vertical = init.vertical;
		this.cbOverflow = init.cbOverflow;

		this.attachShadow(DOMSH.SHADOWDOM_INIT);
		this._initAndInstallSkin(this.localName, init);

		this._start = <div id="start"/> as HTMLDivElement;
		this._end = <div id="end"/> as HTMLDivElement;

		this._content = this.shadowRoot.appendChild(<div id='content'>
			{this._start}
			<slot/>
			{this._end}
		</div> as HTMLDivElement);


		this._startBtn = <Button id='startBtn' ui-context='custom' hidden='' aria-hidden="true"/> as Button;
		this._startBtn.tabIndexCustom = -1; //jamais focusable.
		this._startBtn.icon = '--scroll-startbtn-icon';
		this._startBtn.onclick = () => {this.moveBackward()};
		this._endBtn = <Button id='endBtn' ui-context='custom' hidden='' aria-hidden="true"/> as Button;
		this._endBtn.tabIndexCustom = -1; //jamais focusable.
		this._endBtn.icon = '--scroll-endbtn-icon';
		this._endBtn.onclick = () => {this.moveForward()};

		this.shadowRoot.appendChild(this._startBtn);
		this.shadowRoot.appendChild(this._endBtn);

		// Intersection observer pour mise à jour du scroll
		const intersectObserver = new IntersectionObserver(this.refresh.bind(this), {root: this._content, threshold: [0, 1]});
		intersectObserver.observe(this._start);
		intersectObserver.observe(this._end);

		// Listener sur scroll
		let scrollTimeout: number;
		this._content.addEventListener('scroll', () => {
			if (scrollTimeout) clearTimeout(scrollTimeout);
			scrollTimeout = window.setTimeout(() => {
				requestAnimationFrame(() => {
					this._refreshStates();
					scrollTimeout = null;
				});
			}, 100);
		}, {passive: true});

	}

	protected _refresh() {
		const overflow = this.isOverflow;
		if (overflow) {
			if (DOM.addClass(this, 'scroll')) {
				this._startBtn.hidden = false;
				this._endBtn.hidden = false;
			}
			this._refreshStates();
			if (this.cbOverflow) this.cbOverflow(true);
		} else {
			if (DOM.removeClass(this, 'scroll')) {
				this._startBtn.hidden = true;
				this._endBtn.hidden = true;
				if (this.cbOverflow) this.cbOverflow(false);
			}
		}
		//console.log("refresh:::overflow=", overflow, "hideBtn:::", this._startBtn.hidden, this);
	}

	protected _refreshStates() {
		if (this.vertical) {
			this._startBtn.disabled = this._content.scrollTop === 0;
			this._endBtn.disabled = this._content.scrollTop + this._content.clientHeight >= this._content.scrollHeight;
		} else {
			this._startBtn.disabled = this._content.scrollLeft === 0;
			this._endBtn.disabled = this._content.scrollLeft + this._content.clientWidth >= this._content.scrollWidth;
		}
	}
}

REG.reg.registerSkin('c-arrow-scroll-box', 1, /* language=CSS */ `
	:host {
		position: relative;
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	:host([vertical]) {
		flex-direction: column;
	}

	#content {
		display: flex;
		min-height: 0;
		min-width: 0;
		flex: 1;
		position: relative;
		overflow: hidden;
		scroll-behavior: smooth;
		flex-direction: inherit;
		justify-content: inherit;
	}

	:host(:not([vertical]).scroll) {
		padding-inline-start: var(--icon-size);
		padding-inline-end: var(--icon-size);
		--scroll-startbtn-icon: var(--scroll-hstartbtn-icon, url('/@skin@/commons/icons/scrollArrowLeft.svg'));
		--scroll-endbtn-icon: var(--scroll-hendbtn-icon, url('/@skin@/commons/icons/scrollArrowRight.svg'));
	}

	:host([vertical].scroll) {
		padding-top: var(--icon-size);
		padding-bottom: var(--icon-size);
		--scroll-startbtn-icon: var(--scroll-vstartbtn-icon, url('/@skin@/commons/icons/scrollArrowTop.svg'));
		--scroll-endbtn-icon: var(--scroll-vendbtn-icon, url('/@skin@/commons/icons/scrollArrowBottom.svg'));
	}

	#startBtn {
		position: absolute;
		box-sizing: border-box;
		border: none;
	}

	:host(:not([vertical])) > #startBtn {
		border-inline-end: solid 1px var(--border-color);
		top: 0;
		bottom: 0;
		left: 0;
		width: var(--icon-size);
	}

	:host([vertical]) > #startBtn {
		border-bottom: solid 1px var(--border-color);
		top: 0;
		left: 0;
		right: 0;
		height: var(--icon-size);
	}

	#startBtn[disabled] {
		border-color: transparent;
	}

	#endBtn {
		position: absolute;
		box-sizing: border-box;
		border: none;
	}

	:host(:not([vertical])) > #endBtn {
		border-inline-start: solid 1px var(--border-color);
		right: 0;
		top: 0;
		bottom: 0;
		width: var(--icon-size);
	}

	:host([vertical]) > #endBtn {
		border-top: solid 1px var(--border-color);
		bottom: 0;
		left: 0;
		right: 0;
		height: var(--icon-size);
	}

	#endBtn[disabled] {
		border-color: transparent;
	}
`);

customElements.define('c-arrow-scroll-box', ArrowScrollBox);

/**
 * API équivalente à ArrowScrollBox mais sans aucune gestion d'ArrowScroll.
 */
export interface OrientedBox extends BaseElement {
	initialize(init?: OOrientedBoxInit): this
}

export interface OOrientedBoxInit extends OArrowScrollBoxInit {

	/** Jamais appelé. */
	cbOverflow?: never
}

export class OrientedBox extends BaseElement {

	get vertical(): boolean {return this.hasAttribute('vertical')}

	set vertical(val: boolean) {
		DOM.setAttrBool(this, 'vertical', val);
	}

	/** Compat ArrowScrollBox, mais aucune action. */
	scrollToChild(child: HTMLElement) {}

	get isOverflow() {return false}

	protected _initialize(init: OArrowScrollBoxInit) {
		this.vertical = init.vertical;
		this._attach(this.localName, init, <slot/>);
	}
}

REG.reg.registerSkin('c-oriented-box', 1, /* language=CSS */ `
	:host {
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	:host([vertical]) {
		flex-direction: column;
	}
`);

customElements.define('c-oriented-box', OrientedBox);
